import React from 'react';
import {ChromePicker, type ColorResult} from 'react-color';
import tinycolor from 'tinycolor2';

type Props = {
    id: string;
    onChange: (color: string) => void;
    opened: string | null;
    setOpened: (id: string | null) => void;
    value: string;
    isDisabled?: boolean;
};

type State = {
    focused: boolean;
    isOpened: boolean;
    value: string;
};

export default class ColorInput extends React.PureComponent<Props, State> {
    private colorPicker: React.RefObject<HTMLDivElement>;
    private colorInput: React.RefObject<HTMLInputElement>;

    public constructor(props: Props) {
        super(props);
        this.colorPicker = React.createRef();
        this.colorInput = React.createRef();

        this.state = {
            focused: false,
            isOpened: false,
            value: props.value,
        };
    }

    public componentDidUpdate(prevProps: Props) {
        if (!prevProps.opened && !this.props.opened) {
            return;
        }

        if (this.props.id !== this.props.opened) {
            // eslint-disable-next-line
            this.setState({isOpened: false});
        }
    }

    static getDerivedStateFromProps(props: Props, state: State) {
        if (!state.focused && props.value !== state.value) {
            return {
                value: props.value,
            };
        }

        return null;
    }

    private togglePicker = () => {
        this.props.setOpened(this.state.isOpened ? null : this.props.id);

        this.setState({isOpened: !this.state.isOpened});
    };

    public handleColorChange = (newColorData: ColorResult) => {
        this.props.onChange(newColorData.hex);
    };

    private onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        const color = tinycolor(value);
        const normalizedColor = '#' + color.toHex();

        if (color.isValid()) {
            this.props.onChange(normalizedColor);
        }

        this.setState({value});
    };

    private onFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
        this.setState({
            focused: true,
        });

        if (event.target) {
            event.target.setSelectionRange(1, event.target.value.length);
        }
    };

    private onBlur = () => {
        const value = this.state.value;

        const color = tinycolor(value);
        const normalizedColor = '#' + color.toHex();

        if (color.isValid()) {
            this.props.onChange(normalizedColor);

            this.setState({
                value: normalizedColor,
            });
        } else {
            this.setState({
                value: this.props.value,
            });
        }

        this.setState({
            focused: false,
        });
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // open picker on enter or space
        if (event.key === 'Enter' || event.key === ' ') {
            this.togglePicker();
        }
    };

    public render() {
        const {id} = this.props;
        const {isOpened, value} = this.state;

        return (
            <div className='color-input input-group'>
                <input
                    id={`${id}-inputColorValue`}
                    ref={this.colorInput}
                    className='form-control'
                    type='text'
                    value={value}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                    onFocus={this.onFocus}
                    onKeyDown={this.onKeyDown}
                    maxLength={7}
                    disabled={this.props.isDisabled}
                />
                {!this.props.isDisabled && (
                    <span
                        data-testid={`${id}-squareColorIcon`}
                        id={`${id}-squareColorIcon`}
                        className='input-group-addon color-pad'
                        onClick={this.togglePicker}
                    >
                        <i
                            id={`${id}-squareColorIconValue`}
                            className='color-icon'
                            style={{
                                backgroundColor: value,
                            }}
                        />
                    </span>
                )}
                {isOpened && (
                    <div
                        ref={this.colorPicker}
                        className='color-popover'
                        data-testid={`${id}-ChromePickerModal`}
                        id={`${id}-ChromePickerModal`}
                    >
                        <ChromePicker
                            color={value}
                            onChange={this.handleColorChange}
                            disableAlpha={true}
                        />
                    </div>
                )}
            </div>
        );
    }
}
